import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle, Ruler } from 'lucide-react'
import type { ModifierCategoryPriceImpact } from '@/types'

/**
 * Attach modifier groups to a whole CATEGORY (F-MOD-N5).
 *
 * ⭐ This is a bulk price lever, and the UI treats it as one.
 *
 * Category attachment is LIVE inheritance: every product in the category picks
 * the group up immediately, including ones added later. That is deliberately
 * unlike `default_pct_code` on the same form, which is copied once at product
 * creation — two different semantics on one screen, so both are labelled.
 *
 * Attaching a size group re-bases every product in the category; attaching any
 * required group makes them all unorderable without a choice. The server
 * refuses either with a 409 carrying a per-product before/after preview and
 * writes nothing; PriceImpactDialog is where the owner sees that and decides.
 * Confirmation is never pre-supplied to "save a round trip" — the round trip
 * is the safeguard.
 */

export function CategoryModifierSection({
  value,
  onChange,
}: {
  value: string[]
  onChange: (ids: string[]) => void
}) {
  const { data: allGroups = [], isLoading } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: async () => {
      const res = await apiClient.getModifierGroups()
      if (!res.success) throw new Error(res.message)
      return res.data ?? []
    },
  })

  const active = allGroups.filter((g) => g.is_active)
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id])

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading shared lists…</p>

  if (active.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No shared lists exist yet. Create them under <strong>Menu → Variants</strong>, or open an item and type a question.
      </p>
    )
  }

  const selected = active.filter((g) => value.includes(g.id))
  const movesPrice = selected.filter((g) => g.price_mode === 'replace' || g.min_select >= 1)

  return (
    <div className="space-y-2">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {active.map((g) => (
          <label key={g.id} className="flex items-center gap-2.5 rounded-md border px-3 py-2 text-sm">
            <Checkbox
              checked={value.includes(g.id)}
              onCheckedChange={() => toggle(g.id)}
              aria-label={`Attach ${g.name} to this category`}
            />
            <span className="min-w-0 flex-1 truncate">{g.name}</span>
            {g.price_mode === 'replace' && (
              <Badge variant="outline" className="gap-1 text-[10px]">
                <Ruler className="h-2.5 w-2.5" />
                Sets price
              </Badge>
            )}
            {g.price_mode !== 'replace' && g.min_select >= 1 && (
              <Badge variant="outline" className="text-[10px]">
                Required
              </Badge>
            )}
          </label>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Applies to <strong>every product in this category</strong>, including ones you add
        later. Turn one off for a single item on that item’s own form, or see the whole
        menu at once under <strong>Menu → Variants</strong>.
      </p>

      {movesPrice.length > 0 && (
        <p className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {movesPrice.map((g) => g.name).join(', ')}{' '}
            {movesPrice.length === 1 ? 'changes' : 'change'} what customers pay across this
            category. You will see exactly what changes, per product, before it saves.
          </span>
        </p>
      )}
    </div>
  )
}

/**
 * The confirmation the server demands before a category attachment that moves
 * money is written. Renders the 409's per-product before/after so the decision
 * is made against real numbers rather than a general warning.
 */
export function PriceImpactDialog({
  impact,
  saving,
  onCancel,
  onConfirm,
}: {
  impact: ModifierCategoryPriceImpact
  saving: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const fmt = (n: number) => `₨${n.toFixed(2)}`
  const entries = Object.entries(impact)
  const productCount = entries.reduce((n, [, v]) => n + v.products.length, 0)

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
            This changes what customers are charged
          </DialogTitle>
          <DialogDescription>
            {productCount} product{productCount === 1 ? '' : 's'} in this category
            {productCount === 1 ? ' is' : ' are'} affected. Nothing has been saved yet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {entries.map(([gid, info]) => (
            <div key={gid} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{info.group_name}</span>
                {info.price_mode === 'replace' && (
                  <Badge variant="outline" className="text-[10px]">
                    Replaces the item price
                  </Badge>
                )}
                {info.requires_choice && (
                  <Badge variant="outline" className="text-[10px]">
                    Cashier must choose
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {info.option_count} option{info.option_count === 1 ? '' : 's'}
                </span>
              </div>

              {info.option_count === 0 && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs">
                  This group has no available options. Every product below would become
                  impossible to ring up. Add options first.
                </p>
              )}

              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/70">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Product</th>
                      <th className="px-3 py-2 text-right font-medium">Now</th>
                      <th className="px-3 py-2 text-right font-medium">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.products.map((p) => {
                      const changed =
                        p.min_price_after !== p.current_price ||
                        (p.max_price_after != null && p.max_price_after !== p.current_price)
                      return (
                        <tr key={p.product_id} className="border-t">
                          <td className="px-3 py-1.5">{p.product_name}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                            {fmt(p.current_price)}
                          </td>
                          <td
                            className={
                              'px-3 py-1.5 text-right tabular-nums ' +
                              (changed ? 'font-semibold text-amber-600 dark:text-amber-400' : '')
                            }
                          >
                            {p.max_price_after == null
                              ? `${fmt(p.min_price_after)} and up`
                              : p.min_price_after === p.max_price_after
                                ? fmt(p.min_price_after)
                                : `${fmt(p.min_price_after)} – ${fmt(p.max_price_after)}`}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Don’t change anything
          </Button>
          <Button onClick={onConfirm} disabled={saving}>
            {saving ? 'Saving…' : 'I understand — apply it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
